import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Check, Cpu, ShieldCheck, Wallet } from 'lucide-react'
import { BrandLockup } from '@/components/BrandLogo'
import { isDesktopApp } from '@/services/platform'

/** What the dark panel says when a page does not set its own copy. */
const DEFAULT_PANEL = {
  eyebrow: 'Local AI workspace',
  headline: (
    <>
      Your models and conversations,
      <br />
      on your own computer.
    </>
  ),
  lede: 'Your conversations, installed models, and paired devices in one place. NevTan AI runs open models on hardware you already own.',
  features: [
    {
      icon: <Cpu size={17} />,
      title: 'Hardware aware',
      body: 'Models matched to what your computer can actually run.',
    },
    {
      icon: <ShieldCheck size={17} />,
      title: 'Answers stay local',
      body: 'Local models answer on your own computer.',
    },
    {
      icon: <Wallet size={17} />,
      title: 'No per-message cost',
      body: 'Free to use, with no subscription.',
    },
  ],
}

/**
 * The chrome behind sign-in, sign-up and the password-reset pages.
 *
 * <p>Built the way the rest of the suite builds its account screens: a dark
 * panel on the left carrying the product's case, and the form on a raised card
 * to the right. Below `lg` the left panel is dropped entirely and the card
 * takes the width, with the lockup moving inside it — a phone has no room for
 * a story beside a form.
 */
export function AuthLayout({
  eyebrow = DEFAULT_PANEL.eyebrow,
  headline = DEFAULT_PANEL.headline,
  lede = DEFAULT_PANEL.lede,
  features = DEFAULT_PANEL.features,
  title,
  subtitle,
  assurance = 'Secure workspace sign-in',
  assuranceTone = 'positive',
  backTo = '/',
  footer,
  finePrint,
  children,
}: {
  /** Small bright-teal label above the headline on the dark panel. */
  eyebrow?: string
  headline?: ReactNode
  lede?: string
  features?: { icon: ReactNode; title: string; body: string }[]
  /** The card's own heading. */
  title: string
  subtitle?: string
  /** The strip at the top of the card, above the form. */
  assurance?: string
  /**
   * `caution` for a screen that is reporting a problem. A green strip on an
   * error page tells the reader the opposite of what the page says.
   */
  assuranceTone?: 'positive' | 'caution'
  backTo?: string
  /** Rendered below the card, outside its border. */
  footer?: ReactNode
  finePrint?: string
  children: ReactNode
}) {
  return (
    <div className="flex min-h-full bg-[linear-gradient(#f7f9fc_0%,#eef3f8_48%,#f8fafc_100%)] dark:bg-[linear-gradient(#0a1413_0%,#0e1817_48%,#0a1413_100%)]">
      <aside className="relative hidden overflow-hidden bg-[linear-gradient(145deg,#09233f_0%,#123b5f_46%,#17636c_100%)] px-12 py-10 lg:flex lg:w-[52%] lg:flex-col xl:px-16">
        {/* The suite rules its dark panels with a 72px grid, faint enough to
            read as texture rather than as a table. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,.08)_1px,transparent_1px),linear-gradient(rgba(255,255,255,.07)_1px,transparent_1px)] bg-[length:72px_72px]"
        />

        <div className="relative flex flex-1 flex-col">
          <Link to={backTo} className="inline-flex w-fit items-center" aria-label="NevTan AI, home">
            <BrandLockup tone="white" markClassName="h-9 w-9" textClassName="text-[22px]" />
          </Link>

          <p className="mt-8 text-[13px] font-bold text-[#7ee3d2]">{eyebrow}</p>
          <h2 className="mt-1.5 max-w-[560px] text-[2.4rem] font-extrabold leading-[1.1] tracking-display text-white">
            {headline}
          </h2>
          <p className="mt-3 max-w-[560px] text-[15px] leading-relaxed text-white/[0.78]">{lede}</p>

          <WorkspacePreview />

          <div className="mt-auto grid grid-cols-3 gap-3 pt-8">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="rounded-lg border border-white/[0.16] bg-white/[0.08] p-3"
              >
                <span className="text-[#7ee3d2]">{feature.icon}</span>
                <h3 className="mt-1.5 text-[14px] font-bold text-white">{feature.title}</h3>
                <p className="mt-0.5 text-[12px] leading-relaxed text-white/70">{feature.body}</p>
              </div>
            ))}
          </div>
        </div>
      </aside>

      <div className="relative flex flex-1 flex-col items-center justify-center px-5 py-10 sm:px-10">
        {!isDesktopApp && (
          // The desktop app has no marketing site to return to.
          <Link
            to={backTo}
            className="absolute left-5 top-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand-muted transition-colors hover:text-brand-heading lg:hidden"
          >
            <ArrowLeft size={15} /> Back
          </Link>
        )}

        <div className="w-full max-w-[470px]">
          <div className="rounded-lg border border-brand-line bg-brand-paper/95 p-6 shadow-[0_24px_70px_rgba(15,23,42,.12)] sm:p-7">
            {/* On a narrow screen the dark panel is gone, so the card carries
                the brand itself — on the same navy tile the suite uses, which
                is also the color that panel starts from. */}
            <span className="mb-6 inline-flex rounded-md bg-[#09233f] px-3 py-2 lg:hidden">
              <BrandLockup tone="white" markClassName="h-8 w-8" textClassName="text-[19px]" />
            </span>

            <h1 className="text-[1.75rem] font-extrabold leading-tight tracking-display text-brand-heading sm:text-[2rem]">
              {title}
            </h1>
            {subtitle && <p className="mt-1.5 text-[14px] text-brand-muted">{subtitle}</p>}

            <p
              className={`mt-5 flex items-center gap-2 rounded-md border px-3 py-2 text-[13.5px] font-semibold ${
                assuranceTone === 'caution'
                  ? 'border-[#fde68a] bg-[#fffbeb] text-[#b45309] dark:border-[#78350f] dark:bg-[#231a09] dark:text-[#fcd34d]'
                  : 'border-[#bbf7d0] bg-[#ecfdf5] text-[#047857] dark:border-[#14532d] dark:bg-[#0c1f18] dark:text-[#6ee7b7]'
              }`}
            >
              {assuranceTone === 'caution' ? (
                <AlertTriangle size={15} className="shrink-0" />
              ) : (
                <Check size={15} className="shrink-0" />
              )}
              {assurance}
            </p>

            <div className="mt-5">{children}</div>
          </div>

          {footer}

          {finePrint && (
            <p className="mt-5 text-center text-[12px] leading-relaxed text-brand-muted">
              {finePrint}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * The illustrative panel on the dark side.
 *
 * <p>It names the real sections of the application rather than inventing a
 * screenshot: someone signing in is about to see exactly these.
 */
function WorkspacePreview() {
  return (
    <div className="mt-8 rounded-lg border border-white/[0.18] bg-white/10 p-2">
      <div className="grid gap-2 sm:grid-cols-[150px_1fr]">
        <div className="rounded-md bg-white/[0.06] p-2.5">
          <p className="px-1 pb-1.5 text-[11px] font-bold uppercase tracking-wider text-white/60">
            Workspace
          </p>
          {['Chat', 'Models', 'Projects', 'Settings'].map((item, index) => (
            <p
              key={item}
              className={`rounded px-2 py-1.5 text-[12.5px] ${
                index === 0 ? 'bg-brand-bright/25 font-semibold text-white' : 'text-white/70'
              }`}
            >
              {item}
            </p>
          ))}
        </div>

        <div className="rounded-md bg-white/[0.06] p-2.5">
          {[
            ['Qwen2.5 7B', 'Installed', 'ready'],
            ['Qwen Coder 7B', 'Installed', 'ready'],
            ['DeepSeek R1 8B', 'Available', 'idle'],
          ].map(([name, state, tone]) => (
            <div key={name} className="flex items-center gap-2 px-1 py-2">
              <span
                className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                  tone === 'ready' ? 'bg-brand-bright' : 'bg-white/40'
                }`}
              />
              <span className="min-w-0 flex-1 truncate text-[12.5px] text-white/85">{name}</span>
              <span className="text-[11px] text-white/55">{state}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * One labelled field.
 *
 * <p>The label sits notched into the top border, which is how the suite sets
 * its inputs — and because a leading icon leaves no room for a placeholder to
 * do that job.
 */
export function Field({
  label,
  value,
  onChange,
  type = 'text',
  icon,
  adornment,
  ...rest
}: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  /** Sits inside the field's left edge. */
  icon?: ReactNode
  /** Sits inside the right edge, for a reveal toggle and the like. */
  adornment?: ReactNode
  autoComplete?: string
  placeholder?: string
  required?: boolean
}) {
  return (
    <label className="block">
      <div className="relative">
        <input
          {...rest}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`h-[55px] w-full rounded-md border border-brand-line bg-brand-paper text-[15px] text-brand-heading outline-none transition placeholder:text-brand-muted/70 focus:border-brand focus:ring-4 focus:ring-brand/10 ${
            icon ? 'pl-11' : 'pl-3.5'
          } ${adornment ? 'pr-11' : 'pr-3.5'}`}
        />
        {icon && (
          <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-brand-muted">
            {icon}
          </span>
        )}
        {/* Notched into the border: the white ground hides the line behind it. */}
        <span className="absolute -top-2 left-3 bg-brand-paper px-1 text-[12px] font-medium text-brand-muted">
          {label}
        </span>
        {adornment && <span className="absolute inset-y-0 right-2 flex items-center">{adornment}</span>}
      </div>
    </label>
  )
}
